import React, { useEffect, useState, useCallback } from "react";
import iconSearch from "../../../assets/media/icon-search.svg";
import Navbar from "../../navbar";
import Header from "../../header";
import { useDispatch, useSelector } from "react-redux";
import appLogo from "../../../assets/media/circle-logo.png";
import {
  deleteNotification,
  fetchNotifications,
  updateNotification,
} from "../../../store/reducers/notificationSlice";
import { getallUser } from "../../../store/reducers/userSlice";
import Pagination from "../dashboard/appPagination";
import iconFileDelete from "../../../assets/media/icon-file-delete.svg";
import { Link } from "react-router-dom";
import { showSuccess } from "../../../helpers/toast";
import Modal from "../../modal/Modal";

const Notifications = () => {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(30);
  const user = useSelector((state) => state.auth.user);
  const notifications = useSelector(
    (state) => state.notifications.notifications
  );
  const pagination = useSelector(
    (state) => state.notifications.pagination
  );
  const loading = useSelector(
    (state) => state.notifications.loading
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const getUserall = useSelector((state) => state.usersdata.getalluserdata);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [successMessageShown, setSuccessMessageShown] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedNotificationData, setSelectedNotificationData] =
    useState(null);

  const handleNotificationSelected = (notificationId) => {
    const selectedNotification = notifications.find(
      (notification) => notification.id === notificationId
    );
    setSelectedNotificationData(selectedNotification);
    if (selectedNotification && !selectedNotification.read_status) {
      const updatePayload = {
        notificationId: selectedNotification.id,
        message: selectedNotification.message,
        read_status: true,
        accessToken: user.access_token,
      };
      dispatch(updateNotification(updatePayload));
    }
    setModalVisible(true);
  };

  const getdatafunction = useCallback((page = 1, limit = 30, search = "") => {
    dispatch(getallUser({ authToken: user.access_token }));
    dispatch(fetchNotifications({
      accessToken: user.access_token,
      queryParams: {
        page: page,
        limit: limit,
        search: search
      }
    }));
  }, [dispatch, user.access_token]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const close = () => {
    setSelectedNotificationData(null);
    setModalVisible(false);
    getdatafunction(currentPage, itemsPerPage, debouncedSearchTerm);
  };

  useEffect(() => {
    // Fetch data when page, limit, or search term changes
    getdatafunction(currentPage, itemsPerPage, debouncedSearchTerm);
  }, [dispatch, user, currentPage, itemsPerPage, debouncedSearchTerm, getdatafunction]);

  const handleInputChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    // Reset to page 1 when searching
    setCurrentPage(1);
    // Clear selected notifications when searching
    setSelectedNotifications([]);
  };

  const getUserData = (userId) => {
    const userData = getUserall.find((user) => user.id === userId);
    return userData || { full_name: "Unknown", image: appLogo };
  };

  const BAHAMAS_TIMEZONE = "America/Nassau";

  const getBahamasDateParts = (date) => {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: BAHAMAS_TIMEZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date);

    return {
      year: parts.find((part) => part.type === "year")?.value,
      month: parts.find((part) => part.type === "month")?.value,
      day: parts.find((part) => part.type === "day")?.value,
    };
  };

  const getNotificationTime = (timestamp) => {
    if (!timestamp) return "-";

    const notificationDate = new Date(timestamp);
    if (Number.isNaN(notificationDate.getTime())) return "-";

    const notificationParts = getBahamasDateParts(notificationDate);
    const todayParts = getBahamasDateParts(new Date());
    const isTodayInBahamas =
      notificationParts.year === todayParts.year &&
      notificationParts.month === todayParts.month &&
      notificationParts.day === todayParts.day;

    if (isTodayInBahamas) {
      return notificationDate.toLocaleTimeString("en-US", {
        timeZone: BAHAMAS_TIMEZONE,
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }

    return notificationDate.toLocaleString("en-US", {
      timeZone: BAHAMAS_TIMEZONE,
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleNotificationSelect = (notificationId) => {
    const index = selectedNotifications.indexOf(notificationId);
    if (index === -1) {
      setSelectedNotifications([...selectedNotifications, notificationId]);
    } else {
      setSelectedNotifications(
        selectedNotifications.filter((id) => id !== notificationId)
      );
    }
  };

  const handleSelectAll = () => {
    const allSelected = isAllCurrentPageSelected();
    if (allSelected) {
      // Remove all current page notifications from selection
      const currentPageIds = notifications.map(n => n.id);
      setSelectedNotifications(prev =>
        prev.filter(id => !currentPageIds.includes(id))
      );
    } else {
      // Add all current page notifications to selection
      const currentPageIds = notifications.map(n => n.id);
      setSelectedNotifications(prev => [...new Set([...prev, ...currentPageIds])]);
    }
  };

  // Check if all current page notifications are selected
  const isAllCurrentPageSelected = () => {
    if (notifications.length === 0) return false;
    return notifications.every(notification =>
      selectedNotifications.includes(notification.id)
    );
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Clear selected notifications when changing pages
    setSelectedNotifications([]);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
    setSelectedNotifications([]); // Clear selections
  };

  // We now rely on Redux state for notifications list
  // The fetchNotifications thunk updates state.notifications.notifications

  const deleteSelectedNotifications = () => {
    selectedNotifications.forEach((notificationId) => {
      const config = {
        accessToken: user.access_token,
        notification_ids: notificationId,
      };
      dispatch(deleteNotification({ configdata: config }));
    });
    setSelectedNotifications([]);
    if (!successMessageShown) {
      showSuccess("Notifications deleted successfully");
      setSuccessMessageShown(true);
    }
    // Refresh current page
    getdatafunction(currentPage, itemsPerPage, debouncedSearchTerm);
  };

  return (
    <div>
      <Navbar />
      <Header />
      <div className="wrapper">
        <div className="boxAppBoard boxWhite">
          <div className="boardHeader">
            <div className="boardHeadLeft">
              <h2>Notifications</h2>
            </div>
            <div className="boardHeadRight">
              <div className="searchForm">
                <input
                  type="text"
                  placeholder="Search notifications"
                  onChange={handleInputChange}
                  value={searchTerm}
                />
                <button>
                  <img src={iconSearch} alt="Search" />
                </button>
              </div>
            </div>
          </div>
          {loading ? (
            <div className="text-center my-4 py-4">
              <div className="spinner-border" role="status">
                <span className="sr-only"></span>
              </div>
              <p className="mt-2">Loading notifications...</p>
            </div>
          ) : notifications.length !== 0 ? (
            <div>
              <div className="notification-header-2 px-4 ">
                <div>
                  <input
                    className=""
                    style={{ height: 18 }}
                    type="checkbox"
                    checked={isAllCurrentPageSelected()}
                    onChange={handleSelectAll}
                    id="selectAll"
                  />
                  <label htmlFor="selectAll">Select All</label>
                </div>
                <div>
                  <Link
                    onClick={deleteSelectedNotifications}
                    style={{
                      float: "right",
                      marginRight: 10,
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <img className="mr-2" src={iconFileDelete} alt="" style={{ height: 20 }} />
                    <span className="">Delete</span>
                  </Link>
                </div>
              </div>

              <div className="notification-grid p-4">
                {notifications.map((notification, index) => (
                  <div
                    key={notification.id}
                    className="boardnotification"
                    style={{
                      backgroundColor: notification.read_status === false ? "#f3f3f3" : "white",
                    }}

                  >
                    <div className="notification-header mt-2 mb-2">
                      <input
                        className=""
                        style={{ height: 18 }}
                        type="checkbox"
                        checked={selectedNotifications.includes(
                          notification.id
                        )}
                        onChange={() =>
                          handleNotificationSelect(notification.id)
                        }
                      />

                      <img
                        style={{
                          width: "30px",
                          height: "30px",
                          borderRadius: "50%",
                          border: "1px solid rgb(0 0 0 / 30%)",
                        }}
                        className="avatar"
                        src={
                          getUserData(notification.user_id).image ||
                          appLogo
                        }
                        alt=""
                      />

                      {/* {getUserData(notification.user_id).full_name} */}
                      Notification
                    </div>
                    <div className="notification-time">
                      {getNotificationTime(notification.timestamp)}
                    </div>
                    <div className="notification-message">
                      {notification.message
                        .split(" ")
                        .slice(0, 8)
                        .join(" ") + (notification.message.split(" ").length > 8 ? "..." : "")}

                      <div>
                        <span style={{ cursor: 'pointer' }} onClick={() =>
                          handleNotificationSelected(notification.id)
                        } className="">Read more</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <h2 className="text-danger text-center my-4 py-4">
              Notifications not Found
            </h2>
          )}

          {/* Use pagination data from Redux if available, otherwise fallback to local calculation 
              In server-side pagination mode, we trust the Redux state 
          */}
          {pagination && pagination.totalItems > 0 && (
            <Pagination
              currentPage={currentPage}
              setCurrentPage={handlePageChange}
              totalPages={pagination.totalPages || Math.ceil(pagination.totalItems / itemsPerPage)}
              totalItems={pagination.totalItems}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
        </div>
      </div>
      {
        modalVisible && selectedNotificationData && (
          <Modal
            show={modalVisible}
            handleClose={() => setModalVisible(false)}
            colWidth={6}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <img
                  style={{
                    width: "30px",
                    height: "30px",
                    borderRadius: "50%",
                    marginRight: "10px",
                  }}
                  className="avatar"
                  src={
                    getUserData(selectedNotificationData.user_id).image ||
                    appLogo
                  }
                  alt=""
                />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    flex: 1,
                  }}
                >
                  <strong>
                    {getUserData(selectedNotificationData.user_id).full_name}
                  </strong>
                  <span>
                    {getNotificationTime(selectedNotificationData.timestamp)}
                  </span>
                </div>
              </div>
              <br />
              <div>
                <p style={{ marginLeft: 40 }}>
                  {" "}
                  {selectedNotificationData.message}
                </p>
              </div>
              <div className="uploadRight"></div>
              <div style={{ textAlign: "right" }}>
                <button
                  style={{
                    backgroundColor: "red",
                    color: "white",
                    padding: 5,
                    borderRadius: 40,
                    width: 100,
                  }}
                  onClick={close}
                >
                  Close
                </button>
              </div>
            </div>
          </Modal>
        )
      }
    </div >
  );
};

export default Notifications;
